
import React, { createContext, useContext, useState, useEffect } from "react";
import { User, getUser, saveUser, deleteUser, getAllUsers } from "@/lib/db/indexedDB";
import { useNetworkStatus } from "./NetworkStatusContext";
import { useToast } from "@/components/ui/use-toast";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  login: async () => false,
  register: async () => false,
  logout: () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();

  // Debug function to log all users in IndexedDB
  const debugUsers = async () => {
    try {
      const allUsers = await getAllUsers();
      console.log("All users in IndexedDB:", allUsers);
    } catch (error) {
      console.error("Error retrieving all users:", error);
    }
  };

  // Check for stored credentials on startup
  useEffect(() => {
    const checkStoredAuth = async () => {
      try {
        const storedEmail = localStorage.getItem("userEmail");
        if (storedEmail) {
          console.log("Found stored email in localStorage:", storedEmail);
          const storedUser = await getUser(storedEmail);
          if (storedUser) {
            console.log("Found user in IndexedDB:", storedUser);
            setUser(storedUser);
            // Update last login time
            await saveUser({
              ...storedUser,
              lastLogin: new Date().getTime(),
            });
          } else {
            console.log("No user found in IndexedDB for email:", storedEmail);
          }
        } else {
          console.log("No stored email found in localStorage");
        }
        
        // Debug: Log all users
        await debugUsers();
      } catch (error) {
        console.error("Error checking stored auth:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  // Display online/offline status changes
  useEffect(() => {
    if (user) {
      toast({
        title: isOnline ? "Online mode" : "Offline mode",
        description: isOnline 
          ? "Changes will be synced with the server" 
          : "Working with local data. Changes will sync when connection is restored",
        duration: 3000,
      });
    }
  }, [isOnline, user, toast]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      console.log("Attempting login with email:", email);
      
      // Debug: Log all users before login attempt
      await debugUsers();
      
      // Check local database first
      const localUser = await getUser(email);
      
      console.log("Retrieved user for login:", localUser, "Password entered:", password);
      
      if (localUser && localUser.password === password) {
        console.log("Login successful - passwords match");
        // Update last login
        await saveUser({
          ...localUser,
          lastLogin: new Date().getTime(),
        });
        
        // Store email reference in localStorage for persistent auth
        localStorage.setItem("userEmail", email);
        
        setUser(localUser);
        return true;
      } else {
        console.log("Login failed - user not found or passwords don't match");
        if (localUser) {
          console.log("User exists but password doesn't match");
          console.log("Stored password:", localUser.password);
          console.log("Provided password:", password);
        }
      }
      
      // If online, we would check Supabase here
      if (isOnline) {
        // TODO: Implement Supabase auth when connected
        toast({
          title: "Supabase integration",
          description: "Online verification would happen here when Supabase is connected",
          duration: 3000,
        });
      }
      
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      console.log("Attempting to register with:", { email, password, name });
      
      // Check if user already exists
      const existingUser = await getUser(email);
      if (existingUser) {
        console.log("Registration failed - user already exists:", existingUser);
        toast({
          title: "Registration failed",
          description: "User with this email already exists",
          variant: "destructive",
        });
        return false;
      }
      
      // Create new user locally
      const newUser: User = {
        email,
        password, // In a real app, this would be hashed
        name,
        lastLogin: new Date().getTime(),
        needsSync: true,
      };
      
      console.log("Registering new user:", newUser);
      await saveUser(newUser);
      
      // Debug: Check if user was saved
      const savedUser = await getUser(email);
      console.log("After registration, retrieved user:", savedUser);
      
      // Store email reference in localStorage for persistent auth
      localStorage.setItem("userEmail", email);
      
      setUser(newUser);
      
      // If online, we would also create in Supabase
      if (isOnline) {
        // TODO: Implement Supabase registration when connected
        toast({
          title: "Supabase integration",
          description: "User would also be created in Supabase when online",
          duration: 3000,
        });
      }
      
      return true;
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log("Logging out user:", user?.email);
    localStorage.removeItem("userEmail");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
